import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Users2, Clock } from "lucide-react";

/**
 * "Chi siamo" — Skeleton only. Until an admin publishes real content (via
 * PUT /api/pages/chi-siamo with published=true, title, body_md, contacts,
 * people, …) we show a sober placeholder as mandated by the product spec:
 * no invented biographies, roles, companies or collaborators.
 *
 * The backend endpoint accepts a `?lang=` query and returns the requested
 * translation from the multilingual `pages` document (fallback IT → any).
 */
const PLACEHOLDER = "Stiamo preparando questa sezione per raccontarti in modo trasparente chi ha dato vita a ToClean e come nasce il progetto.";

export default function AboutPage() {
  const { lang } = useI18n();
  const [page, setPage] = useState(null);
  useEffect(() => {
    // Accept the admin override only if the backend actually served the
    // requested language. Otherwise (fallback to IT), stay on the
    // untranslated placeholder so the page never turns monolingual IT
    // when the admin has only compiled the Italian version.
    let cancel = false;
    api.get(`/pages/chi-siamo?lang=${encodeURIComponent(lang)}`)
      .then((r) => {
        if (cancel) return;
        const d = r.data || null;
        if (!d || !d.published) { setPage(d); return; }
        const langMatches = !d.lang || d.lang === lang;
        setPage(langMatches ? d : { ...d, published: false });
      })
      .catch(() => { if (!cancel) setPage(null); });
    return () => { cancel = true; };
  }, [lang]);

  const isPublished = !!(page && page.published);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}>
        <BackButton hideMap />
        <div className="inline-flex items-center gap-2 chip bg-emerald-50 text-emerald-800 border border-emerald-200 mt-3 mb-3">
          <Users2 size={12} /> Chi siamo
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight" data-testid="about-title">
          {(isPublished && page.title) || "Chi siamo"}
        </h1>

        {isPublished ? (
          <>
            {page.body_md && (
              <div
                data-testid="about-body"
                className="mt-5 prose prose-slate max-w-none text-slate-700 whitespace-pre-line text-base leading-relaxed"
              >{page.body_md}</div>
            )}
            {page.people && (
              <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid="about-people">
                <div className="font-heading font-semibold text-slate-900 mb-2">Persone coinvolte</div>
                <div className="text-slate-700 whitespace-pre-line text-sm sm:text-base">{page.people}</div>
              </div>
            )}
            {page.contacts && (
              <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid="about-contacts">
                <div className="font-heading font-semibold text-slate-900 mb-2">Contatti</div>
                <div className="text-slate-700 whitespace-pre-line text-sm sm:text-base">{page.contacts}</div>
              </div>
            )}
            {page.updated_at && (
              <div className="mt-4 text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid="about-updated-at">
                <Clock size={11} /> Ultimo aggiornamento: {new Date(page.updated_at).toLocaleDateString("it-IT")}
              </div>
            )}
          </>
        ) : (
          <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 sm:p-7 text-center" data-testid="about-placeholder">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
              <Clock size={20} />
            </div>
            <p className="text-slate-700 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
              {PLACEHOLDER}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
